import React from "react";
import { Bot, CalendarClock, FileText, Mic } from "lucide-react";

const LEA_EVENTS = new Set([
  "lea_call_queued", "lea_call_started", "lea_call_completed", "lea_call_failed",
  "lea_summary_available", "qualification_successful", "qualification_inconclusive",
  "callback_requested", "interview_ready",
]);

const EVENT_LABELS = {
  lea_call_queued: "Appel mis en file",
  lea_call_started: "Appel démarré",
  lea_call_completed: "Appel terminé",
  lea_call_failed: "Appel échoué",
  lea_summary_available: "Résumé disponible",
  qualification_successful: "Qualification réussie",
  qualification_inconclusive: "Qualification non concluante",
  callback_requested: "Rappel demandé",
  interview_ready: "Prêt pour entrevue",
};

const DISPOSITIONS = {
  "no-answer": "Sans réponse",
  "hung-up": "Raccroché",
  answered: "Répondu",
  unknown: "Inconnue",
};

const fmtDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" });
};

const fmtList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(" · ") || null;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const Field = ({ testId, label, value }) => {
  const text = fmtList(value);
  if (!text) return null;
  return (
    <div data-testid={testId} className="space-y-0.5">
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm">{text}</dd>
    </div>
  );
};

export const LeaResultPanel = ({ applicationId, result, scheduling, timeline }) => {
  const hasResult = Boolean(result && (result.received_at || result.source_call_id));
  const events = (timeline || []).filter((e) => LEA_EVENTS.has(e.event));

  if (!hasResult) {
    return (
      <div
        data-testid={`lea-result-empty-${applicationId}`}
        className="border border-border bg-secondary/30 px-3 py-3 text-xs text-muted-foreground"
      >
        Aucun résultat Léa pour cette candidature.
      </div>
    );
  }

  const readyNotBooked = result.interview_ready === true && !scheduling?.calendar_connected;

  return (
    <section
      data-testid={`lea-result-panel-${applicationId}`}
      aria-label="Résultat Léa"
      className="space-y-3 border border-border bg-secondary/30 px-3 py-3"
    >
      <header className="flex flex-wrap items-center gap-2">
        <Bot className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
        <h3 className="text-[11px] font-medium uppercase tracking-wider">Résultat Léa</h3>
        <span
          data-testid={`lea-result-provenance-${applicationId}`}
          className="border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground"
        >
          Source : Léa / VytaliLeads · actor_type : ai · {result.extraction_provenance || "lea_conversation"}
        </span>
      </header>

      <p className="text-[11px] text-muted-foreground">
        Données produites par un agent conversationnel. Il ne s'agit pas d'une décision humaine.
      </p>

      <div className="flex flex-wrap gap-2">
        {result.call_status && (
          <span data-testid={`lea-result-call-status-${applicationId}`}
            className="border border-border bg-surface px-2 py-0.5 text-[11px] uppercase tracking-wider">
            {result.call_status}
          </span>
        )}
        {result.disposition && (
          <span data-testid={`lea-result-disposition-${applicationId}`}
            className="border border-border bg-surface px-2 py-0.5 text-[11px] uppercase tracking-wider">
            {DISPOSITIONS[result.disposition] || result.disposition}
          </span>
        )}
        {result.qualification_status && (
          <span data-testid={`lea-result-qualification-${applicationId}`}
            className={`border px-2 py-0.5 text-[11px] uppercase tracking-wider ${
              result.qualification_status === "successful"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-border bg-surface text-muted-foreground"
            }`}>
            {result.qualification_status === "successful" ? "Qualification réussie" : result.qualification_status}
          </span>
        )}
        {result.sentiment && (
          <span data-testid={`lea-result-sentiment-${applicationId}`}
            className="border border-border bg-surface px-2 py-0.5 text-[11px] uppercase tracking-wider">
            Sentiment : {result.sentiment}
          </span>
        )}
        {result.callback_requested === true && (
          <span data-testid={`lea-result-callback-${applicationId}`}
            className="border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-900">
            Rappel demandé
          </span>
        )}
        {result.human_followup_required === true && (
          <span data-testid={`lea-result-followup-${applicationId}`}
            className="border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-900">
            Suivi humain requis
          </span>
        )}
      </div>

      {readyNotBooked && (
        <p data-testid={`lea-result-interview-ready-${applicationId}`}
          className="flex items-center gap-2 border border-border bg-surface px-2 py-1.5 text-xs">
          <CalendarClock className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
          Prêt pour entrevue — rendez-vous non encore confirmé
        </p>
      )}

      <dl className="grid gap-3 sm:grid-cols-2">
        <Field testId={`lea-result-summary-${applicationId}`} label="Résumé de qualification"
          value={result.qualification_summary} />
        <Field testId={`lea-result-interest-${applicationId}`} label="Intérêt du candidat"
          value={result.candidate_interest} />
        <Field testId={`lea-result-availability-${applicationId}`} label="Disponibilité (dite à Léa)"
          value={result.availability} />
        <Field testId={`lea-result-motivations-${applicationId}`} label="Motivations"
          value={result.motivations} />
        <Field testId={`lea-result-experience-${applicationId}`} label="Expérience pertinente confirmée"
          value={result.relevant_experience_confirmed} />
        <Field testId={`lea-result-concerns-${applicationId}`} label="Préoccupations"
          value={result.concerns} />
        <Field testId={`lea-result-questions-${applicationId}`} label="Questions du candidat"
          value={result.candidate_questions} />
        <Field testId={`lea-result-next-step-${applicationId}`} label="Prochaine étape recommandée"
          value={result.recommended_next_step} />
        <Field testId={`lea-result-duration-${applicationId}`} label="Durée (s)"
          value={result.duration_seconds} />
        <Field testId={`lea-result-received-${applicationId}`} label="Reçu le"
          value={fmtDate(result.received_at)} />
        <Field testId={`lea-result-call-id-${applicationId}`} label="Call ID"
          value={result.call_id || result.source_call_id} />
      </dl>

      {(result.transcript_reference || result.recording_reference) && (
        <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          {result.transcript_reference && (
            <span data-testid={`lea-result-transcript-${applicationId}`} className="inline-flex items-center gap-1.5">
              <FileText className="h-3 w-3" aria-hidden="true" /> {result.transcript_reference}
            </span>
          )}
          {result.recording_reference && (
            <span data-testid={`lea-result-recording-${applicationId}`} className="inline-flex items-center gap-1.5">
              <Mic className="h-3 w-3" aria-hidden="true" /> {result.recording_reference}
            </span>
          )}
        </div>
      )}

      {events.length > 0 && (
        <div data-testid={`lea-result-timeline-${applicationId}`} className="space-y-1 border-t border-border pt-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Événements Léa</p>
          <ul className="space-y-1">
            {events.map((event) => (
              <li key={event.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                <span>{EVENT_LABELS[event.event] || event.event}</span>
                <span className="text-[11px] text-muted-foreground">{fmtDate(event.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
